import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const EXP_KEY = "rd_discount_exp";

const StorefrontContext = createContext({
    settings: null,
    discountCode: "",
    setDiscountCode: () => {},
    discountExpiresAt: null,
    startDiscountTimer: () => null,
});

const readLS = (key) => {
    try {
        return localStorage.getItem(key) || "";
    } catch {
        return "";
    }
};

const writeLS = (key, value) => {
    try {
        if (value) localStorage.setItem(key, value);
        else localStorage.removeItem(key);
    } catch {
        /* ignore */
    }
};

export const campaignEndsAt = (settings) =>
    settings?.exit_discount_ends_at ? new Date(settings.exit_discount_ends_at).getTime() : null;

export function StorefrontProvider({ children }) {
    const [settings, setSettings] = useState(null);
    const [discountCode, setDiscountCode] = useState(() => readLS("rd_discount"));
    const [discountExpiresAt, setDiscountExpiresAt] = useState(() => Number(readLS(EXP_KEY)) || null);

    useEffect(() => {
        api.get("/storefront/settings")
            .then((r) => setSettings(r.data))
            .catch(() => setSettings({ show_exit_intent: false }));
    }, []);

    useEffect(() => {
        const token = settings?.google_site_verification;
        if (!token) return;
        let el = document.head.querySelector('meta[name="google-site-verification"]');
        if (!el) {
            el = document.createElement("meta");
            el.setAttribute("name", "google-site-verification");
            document.head.appendChild(el);
        }
        el.setAttribute("content", token);
    }, [settings]);

    useEffect(() => {
        if (settings && !settings.show_exit_intent && discountCode) {
            setDiscountCode("");
            setDiscountExpiresAt(null);
        }
    }, [settings, discountCode]);

    useEffect(() => writeLS("rd_discount", discountCode), [discountCode]);
    useEffect(() => writeLS(EXP_KEY, discountExpiresAt ? String(discountExpiresAt) : ""), [discountExpiresAt]);

    const startDiscountTimer = useCallback(() => {
        if (discountExpiresAt && discountExpiresAt > Date.now()) return discountExpiresAt;
        const minutes = Number(settings?.exit_discount_duration_minutes || 15);
        const ends = campaignEndsAt(settings);
        const exp = Math.min(Date.now() + minutes * 60000, ends ?? Infinity);
        setDiscountExpiresAt(exp);
        return exp;
    }, [settings, discountExpiresAt]);

    useEffect(() => {
        if (!settings) return;
        const ends = campaignEndsAt(settings);
        const exp = Math.min(discountExpiresAt ?? Infinity, ends ?? Infinity);
        if (exp === Infinity) return;
        const expire = () => {
            setDiscountCode("");
            setDiscountExpiresAt(null);
        };
        const ms = exp - Date.now();
        if (ms <= 0) {
            if (discountCode) expire();
            return;
        }
        const t = setTimeout(expire, ms);
        return () => clearTimeout(t);
    }, [settings, discountExpiresAt, discountCode]);

    return (
        <StorefrontContext.Provider
            value={{ settings, discountCode, setDiscountCode, discountExpiresAt, startDiscountTimer }}
        >
            {children}
        </StorefrontContext.Provider>
    );
}

export const useStorefront = () => useContext(StorefrontContext);
